import { deleteDoc, doc } from 'firebase/firestore';
import type { Draft, SupportDoc } from '../../components/TenderDetails/types';
import { updateDocumentCategory } from '../../services/api';
import { firestore } from '../../services/firebase';
import type { User } from '../../types/auth';
import type { Tender } from '../../types/tender';
import { logDocumentActivity } from '../../utils/activityLogger';

export interface UseSupportDocsOverridesProps {
  tender: Tender | null;
  account: (User & { clientId?: string; actualDefaultBucketName?: string }) | null;
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void;
  sortedDocs: (SupportDoc | Draft)[];
  tenderCVs: SupportDoc[];
  setDocumentCategories: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export const useSupportDocsOverrides = ({
  tender,
  account,
  showSnackbar,
  sortedDocs,
  tenderCVs,
  setDocumentCategories,
}: UseSupportDocsOverridesProps) => {
  const handleCategoryChange = async (
    docIdentifier: string,
    newCategory: string,
    role?: string,
    specialization?: string
  ) => {
    if (!account || !tender || !account.clientId) {
      return;
    }

    const foundDoc = sortedDocs.find(
      (d) =>
        (d.originalFilename || d.filename || d.name)?.replace(/\.[^/.]+$/, '') === docIdentifier
    );
    if (!foundDoc) {
      showSnackbar('Document not found.', 'error');
      return;
    }

    const isDraft = 'filename' in foundDoc;
    const docName = (foundDoc.originalFilename || foundDoc.filename || foundDoc.name)?.replace(
      /\.[^/.]+$/,
      ''
    );

    if (!docName) {
      showSnackbar('Invalid document name.', 'error');
      return;
    }

    try {
      if (tender.id) {
        await updateDocumentCategory({
          clientId: account.clientId,
          userId: account.uid,
          tenderId: tender.id,
          docName: docName,
          docType: newCategory,
          isDraft,
          role,
          specialization,
        });
        setDocumentCategories((prev) => ({ ...prev, [docIdentifier]: newCategory }));
        showSnackbar('Category updated successfully!', 'success');
        logDocumentActivity.updated({
          fileName: docName,
          type: newCategory,
          source: `tender_details/${tender.id}`,
        });

        const isCV =
          newCategory.toLowerCase() === 'cv' ||
          newCategory.toLowerCase() === 'resume/cv' ||
          newCategory.toLowerCase() === 'people_cv' ||
          newCategory.toLowerCase() === 'cvs';
        if (!isCV) {
          const matchingCV = tenderCVs.find((cv) => cv.originalFilename === docName);
          if (matchingCV) {
            const cvId = matchingCV.id || docName;
            const cvRef = doc(firestore, 'tenders', tender.id, 'cvs', cvId);
            try {
              await deleteDoc(cvRef);
            } catch (_e) {
              // Ignore
            }
          }
        }
      } else {
        showSnackbar('Tender ID not found.', 'error');
      }
    } catch (_error) {
      showSnackbar('Failed to update category.', 'error');
    }
  };

  return {
    handleCategoryChange,
  };
};
